import { __assign, __awaiter, __generator } from "tslib";
import { mmUid } from 'mm-ts-utils';
import { generateNewThreadId } from '../../client/src/_library/utils/identifiers';
import { CUSTOM_MESSAGE_ROOM_MARKER__SET_ENTITIES, CUSTOM_MESSAGE_ROOM_MARKER__SET_STATE, CUSTOM_MESSAGE_ROOM_MARKER__SET_STATE_AND_ENTITIES, DOMAIN_TYPE_CHAT_MESSAGE, GOODBOT_USER_ID, NOTIFICATION_ID__THREAD_CHANGE, NOTIFICATION_ID__THREAD_CREATED, } from '../../client/src/consts';
import { apiNormalizeJsonApiResponse } from '../../client/src/modules/_core/utils/api-helpers';
import { ChatMessage } from '../../client/src/modules/chat/ChatMessage';
import { clientNotification } from '../../client/src/modules/domain/domain-helpers';
import HttpClient from '../http-client';
import { sendCurrentConversationIdToParent } from '../../client/src/modules/_core/components/BubbleChannel';
var GoodBotFlowChat = /** @class */ (function () {
    function GoodBotFlowChat(httpClient) {
        var _this = this;
        /**
         * https://docs.partner.borndigital.ai/api_flow.html#get-gurl-flows-config-hash-conversations-cid-messages
         */
        this.readThread = function (threadId) { return __awaiter(_this, void 0, void 0, function () {
            var url, response;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        url = "/conversations/".concat(threadId, "/messages");
                        return [4 /*yield*/, this.httpClient.get(url)];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, apiNormalizeJsonApiResponse({
                                data: response.data
                                    .map(GoodBotFlowChat._convertMessageWithMultipleChoices)
                                    // initial message (to start conversation) has no `content` & no `options`
                                    .filter(function (m) { return m.content || (m.options && m.options.length > 0); })
                                    .map(function (message) {
                                    var isBot = message.source === 'GoodBot';
                                    return {
                                        id: message.id,
                                        type: DOMAIN_TYPE_CHAT_MESSAGE,
                                        attributes: {
                                            id: message.id,
                                            // This ID distinguishes messages from user and GoodBot.
                                            // The field `is_bot` is ignored and is obsolete.
                                            // See the `BaseChatMessage.is_bot` and `GOODBOT_USER_ID` and their usages.
                                            user_id: isBot ? GOODBOT_USER_ID : undefined,
                                            thread_id: threadId,
                                            is_bot: isBot,
                                            type: message.type,
                                            body: message.content,
                                            // We used to use `created: message.timestamp` but the problem
                                            // is that the user can have different time and then ordering of
                                            // the messages is broken. We use browser time for everything now.
                                            // See https://trello.com/c/G8jSe9B6/287-messages-ordering-is-weird-in-chat-bubble
                                            created: new Date(),
                                            meta: {
                                                data: message.additionalData,
                                                options: message.options || [],
                                            },
                                        },
                                    };
                                }),
                                meta: __assign(__assign({}, response.meta), { config_hash: response.config_hash }),
                                included: [
                                    clientNotification(NOTIFICATION_ID__THREAD_CHANGE, {
                                        thread_id: threadId,
                                        created: new Date(),
                                    }),
                                ],
                            })];
                }
            });
        }); };
        this.createThread = function (threadId, api_token, options) {
            if (threadId === void 0) { threadId = generateNewThreadId(); }
            return __awaiter(_this, void 0, void 0, function () {
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            // This should be a place where currentConversationId/threadId is generated for nlp purposes,
                            // in other words this ID can be found in technical logs
                            threadId = threadId || generateNewThreadId();
                            sendCurrentConversationIdToParent(threadId);
                            return [4 /*yield*/, this.postUtterance(threadId, '', '', null, options)];
                        case 1: return [2 /*return*/, _a.sent()];
                    }
                });
            });
        };
        this.saveMessage = function (messageData) {
            return _this.postUtterance(messageData.thread_id, (messageData.meta || {}).utterance || messageData.body, messageData.user_id, messageData);
        };
        /**
         * When the `utterance` is empty string it means "start a new conversation thread".
         * https://docs.partner.borndigital.ai/api_flow.html#post-gurl-flows-config-hash-conversations-cid-messages
         */
        this.postUtterance = function (threadId, utterance, userId, messageData, options) {
            if (utterance === void 0) { utterance = ''; }
            return __awaiter(_this, void 0, void 0, function () {
                var interactionId, Location, parsed, payload_1, currentHttpClient, oldBaseURL, modifiedBaseUrl, url_1, response_1, payload, url, response, includedEntities;
                var _a, _b, _c;
                return __generator(this, function (_d) {
                    switch (_d.label) {
                        case 0:
                            interactionId = (messageData && messageData.id) || mmUid();
                            Location = ((_a = options === null || options === void 0 ? void 0 : options.initialData) === null || _a === void 0 ? void 0 : _a.Location) || '';
                            parsed = tryParseJsonLikeObject(utterance);
                            if (!(Location || parsed.room)) return [3 /*break*/, 2];
                            payload_1 = {
                                channel_id: 'text',
                                message_id: interactionId,
                                user_id: userId !== null && userId !== void 0 ? userId : '',
                                named_entities: __assign({}, (Location ? { Location: Location } : {})),
                            };
                            if (parsed.room === CUSTOM_MESSAGE_ROOM_MARKER__SET_ENTITIES) {
                                payload_1['named_entities'] = __assign(__assign({}, payload_1.named_entities), parsed.payload);
                            }
                            if (parsed.room === CUSTOM_MESSAGE_ROOM_MARKER__SET_STATE) {
                                payload_1 = __assign(__assign({}, payload_1), parsed.payload);
                            }
                            if (parsed.room === CUSTOM_MESSAGE_ROOM_MARKER__SET_STATE_AND_ENTITIES) {
                                payload_1 = __assign(__assign({}, payload_1), { named_entities: __assign(__assign({}, payload_1.named_entities), parsed.payload.named_entities) });
                                payload_1['new_state'] = parsed.payload.new_state;
                            }
                            currentHttpClient = this.httpClient;
                            // Create new HTTPCient with new BASE URL if the config hash is changed
                            if ((_b = parsed === null || parsed === void 0 ? void 0 : parsed.payload) === null || _b === void 0 ? void 0 : _b.config_hash) {
                                oldBaseURL = this.httpClient.getBaseUrl;
                                modifiedBaseUrl = oldBaseURL.replace(/\/flows\/.*/, "/flows/".concat(parsed.payload.config_hash));
                                currentHttpClient = new HttpClient(modifiedBaseUrl);
                            }
                            url_1 = "/conversations/".concat((_c = parsed.threadId) !== null && _c !== void 0 ? _c : threadId, "/states");
                            return [4 /*yield*/, currentHttpClient.put(url_1, payload_1, {
                                    'X-Interaction-Id': interactionId,
                                }, messageData === null || messageData === void 0 ? void 0 : messageData.meta)];
                        case 1:
                            response_1 = _d.sent();
                            if (response_1.error) {
                                throw new Error(response_1.error);
                            }
                            if (parsed && parsed.room)
                                return [2 /*return*/, {}];
                            _d.label = 2;
                        case 2:
                            payload = {
                                channel_id: 'text',
                                utterance: utterance,
                                message_id: interactionId,
                                user_id: userId !== null && userId !== void 0 ? userId : undefined,
                            };
                            url = "/conversations/".concat(threadId, "/messages");
                            return [4 /*yield*/, this.httpClient.post(url, payload, {
                                    'X-Interaction-Id': interactionId,
                                })];
                        case 3:
                            response = _d.sent();
                            if (response.error) {
                                throw new Error(response.error);
                            }
                            includedEntities = [];
                            if (utterance) {
                                includedEntities.push(new ChatMessage(messageData).toJSONApi(), clientNotification(NOTIFICATION_ID__THREAD_CHANGE, {
                                    thread_id: threadId,
                                    created: new Date(),
                                }));
                            }
                            else {
                                // no utterance sent = new tread, we notify that thread was created
                                includedEntities.push(clientNotification(NOTIFICATION_ID__THREAD_CREATED, {
                                    thread_id: threadId,
                                }));
                            }
                            return [2 /*return*/, apiNormalizeJsonApiResponse({
                                    data: response.body
                                        .map(GoodBotFlowChat._convertMessageWithMultipleChoices)
                                        .map(function (message) { return ({
                                        id: message.id,
                                        type: DOMAIN_TYPE_CHAT_MESSAGE,
                                        attributes: {
                                            id: message.id,
                                            user_id: GOODBOT_USER_ID,
                                            thread_id: threadId,
                                            is_bot: true,
                                            type: message.type,
                                            body: message.content,
                                            // We used to use `created: message.timestamp` but the problem
                                            // is that the user can have different time and then ordering of
                                            // the messages is broken. We use browser time for everything now.
                                            // See https://trello.com/c/G8jSe9B6/287-messages-ordering-is-weird-in-chat-bubble
                                            created: new Date(),
                                            meta: {
                                                data: message.additionalData,
                                                options: message.options || [],
                                            },
                                        },
                                    }); }),
                                    meta: __assign(__assign({}, response.meta), { config_hash: response.config_hash }),
                                    included: includedEntities,
                                })];
                    }
                });
            });
        };
        this.httpClient = httpClient;
    }
    GoodBotFlowChat._convertMessageWithMultipleChoices = function (message) {
        if (message.type === 'multiple_choices') {
            return __assign(__assign(__assign(__assign({}, message), message.content.markdown), message.content.widget), { additionalData: message.content.data && message.content.data.content });
        }
        else {
            return message;
        }
    };
    return GoodBotFlowChat;
}());
export default GoodBotFlowChat;
function tryParseJsonLikeObject(item) {
    try {
        return JSON.parse(item);
    }
    catch (e) {
        return false;
    }
}
